import React, { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
    EMPLOYMENT_STATUSES,
    EMPLOYMENT_STATUS_LABEL,
} from "./EmploymentStatusBadge";
import EmploymentStatusBadge from "./EmploymentStatusBadge";

// Iter60.p63 — Statuses that trigger the annual-leave payout hook.
const OFFBOARDED_STATUSES = new Set([
    "project_completed",
    "contract_ended",
    "resigned",
    "terminated",
    "retired",
]);

// Iter60.p59 — Change Status dialog used by both:
//   1. Single-row kebab action on Staff (mode="single")
//   2. Bulk toolbar action on Staff (mode="bulk")
//
// Props:
//   open:        boolean
//   onOpenChange: (bool) => void
//   employees:   [{id, full_name, module_employee_id, employment_status}]  (1..N)
//   currentStatus: string   (in single mode, drives the "no-change" guard)
//   onSaved:     () => void  fires on success; caller reloads
export default function ChangeStatusDialog({
    open,
    onOpenChange,
    employees = [],
    currentStatus,
    onSaved,
}) {
    const [status, setStatus] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    // Iter60.p63 — payout preview for single-mode active→off-boarded flips.
    const [payoutPreview, setPayoutPreview] = useState(null);
    const [payoutLoading, setPayoutLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setStatus(currentStatus && currentStatus !== "active" ? "active" : "");
            setNote("");
            setPayoutPreview(null);
        }
    }, [open, currentStatus]);

    const isBulk = employees.length > 1;
    const empLabel = isBulk
        ? `${employees.length} employees`
        : (employees[0]?.full_name || employees[0]?.module_employee_id || "employee");

    // Iter60.p63 — Fetch the payout preview when: single mode, current
    // status is `active`, chosen new status is off-boarded, and we
    // haven't already fetched it. Silent-fail (missing hire_date etc.
    // still lets the flip proceed).
    useEffect(() => {
        if (isBulk || !employees[0]?.id) return;
        const shouldFetch =
            currentStatus === "active"
            && status
            && OFFBOARDED_STATUSES.has(status);
        if (!shouldFetch) {
            setPayoutPreview(null);
            return;
        }
        setPayoutLoading(true);
        api
            .get(`/employees/${employees[0].id}/employment-end-payout-preview`)
            .then(({ data }) => setPayoutPreview(data))
            .catch(() => setPayoutPreview(null))
            .finally(() => setPayoutLoading(false));
    }, [status, currentStatus, isBulk, employees]);

    const submit = async () => {
        if (!status) {
            toast.error("Pick a status first");
            return;
        }
        setBusy(true);
        try {
            if (isBulk) {
                const { data } = await api.post("/employees/status/bulk", {
                    employee_ids: employees.map((e) => e.id),
                    status,
                    note: note || null,
                });
                if (data.skipped?.length) {
                    toast.warning(
                        `Updated ${data.updated} · Skipped ${data.skipped.length}. `
                        + `First: ${data.skipped[0].reason}`,
                    );
                } else {
                    toast.success(`Updated ${data.updated} employee${data.updated === 1 ? "" : "s"}.`);
                }
            } else {
                await api.patch(`/employees/${employees[0].id}/status`, {
                    status,
                    note: note || null,
                });
                toast.success(`Status changed to "${EMPLOYMENT_STATUS_LABEL[status]}"`);
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="change-status-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="change-status-title">
                        Change Employment Status
                    </DialogTitle>
                    <DialogDescription>
                        {isBulk ? (
                            <>Flipping <span className="font-semibold">{empLabel}</span>. Any employees outside your module scope will be skipped automatically.</>
                        ) : (
                            <>Flipping <span className="font-semibold">{empLabel}</span>. The change is logged in the employee's audit trail.</>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1">
                        <Label className="text-xs uppercase">New status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="change-status-select">
                                <SelectValue placeholder="Pick a status…" />
                            </SelectTrigger>
                            <SelectContent>
                                {EMPLOYMENT_STATUSES.map((s) => (
                                    <SelectItem key={s} value={s} data-testid={`change-status-opt-${s}`}>
                                        <span className="inline-flex items-center gap-2">
                                            <EmploymentStatusBadge status={s} size="xs" testid={`change-status-preview-${s}`} />
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase">
                            Note <span className="text-[color:var(--asl-muted)]">(optional)</span>
                        </Label>
                        <Input
                            placeholder="e.g., Contract ended 2026-08-31, project 5050"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            maxLength={500}
                            data-testid="change-status-note"
                        />
                    </div>
                    {/* Iter60.p63 — Annual leave payout preview */}
                    {!isBulk && payoutPreview && !payoutPreview.missing_hire_date && payoutPreview.unused_days > 0 && (
                        <div
                            className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3 space-y-1"
                            data-testid="change-status-payout-preview"
                        >
                            <div className="text-xs font-semibold asl-crimson-text">
                                Annual leave payout — Somali Labor Law Art. 69
                            </div>
                            <p className="text-xs">
                                This employee has{" "}
                                <span className="font-semibold asl-navy-text" data-testid="change-status-payout-days">
                                    {payoutPreview.unused_days} unused annual leave day{payoutPreview.unused_days === 1 ? "" : "s"}
                                </span>.
                                {" "}On confirm, a payout of{" "}
                                <span className="font-semibold asl-crimson-text" data-testid="change-status-payout-amount">
                                    ${payoutPreview.payout_amount.toFixed(2)}
                                </span>{" "}
                                ({payoutPreview.unused_days} × ${payoutPreview.daily_rate.toFixed(2)}/day)
                                will be created — attached to the next open payroll period if one exists,
                                otherwise queued for HR to attach manually.
                            </p>
                        </div>
                    )}
                    {!isBulk && payoutPreview && payoutPreview.missing_hire_date && (
                        <div
                            className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3"
                            data-testid="change-status-payout-missing-hire-date"
                        >
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                No <span className="mono">hire_date</span> on record — annual leave payout skipped.
                                The status flip will still proceed.
                            </p>
                        </div>
                    )}
                    {!isBulk && payoutLoading && (
                        <p
                            className="text-xs text-[color:var(--asl-muted)] italic"
                            data-testid="change-status-payout-loading"
                        >
                            Computing annual leave payout…
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                            data-testid="change-status-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !status}
                        className="asl-navy-bg text-white"
                        data-testid="change-status-submit"
                    >
                        {busy ? "Saving…" : (isBulk ? `Update ${employees.length}` : "Update")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
